import React from 'react';
import ActivityMediumBox from '../Box/ActivityMediumBox';
import ActivitySmallBox from '../Box/ActivitySmallBox';
import ActivityLargeBox from '../Box/ActivityLargeBox';
import ActivityLongBox from '../Box/ActivityLongBox';
import NoDataBox from '../Box/NoDataBox';
import ActivityChart from '../Chart/PieChart';
import ActivityLegend from '../Chart/ActivityLegend';
import SleepLegend from '../Chart/SleepLegend';
import ActivityCenterComponent from '../Chart/ActivityCenterPie';
import SleepCenterComponent from '../Chart/SleepCenterPie';
import Accessibility from '../accessibility';

import IconPin from '../../../../images/Presense/Icon/pin.svg';
import IconStep from '../../../../images/Presense/Icon/step.svg';
import IconDistance from '../../../../images/Presense/Icon/distance.svg';
import { getTimeFormatted } from '../../../../utils/timeFormatted';
import * as ActionColors from '../../../../themes/summaryReportColor';

import {
  HeartRateLevel,
  TemperatureLevel,
  SpO2Level,
  BloodPressureLevel,
  // RespirationRateLevel,
} from '../VitalSign/Level';

const activityColor = {
  walking: ActionColors.COLOR_WALKING,
  standing: ActionColors.COLOR_STANDING,
  sitting: ActionColors.COLOR_SITTING,
  lying: ActionColors.COLOR_LYING,
  lyingOnLeft: ActionColors.COLOR_LYING_ON_LEFT_SIDE,
  lyingOnRight: ActionColors.COLOR_LYING_ON_RIGHT_SIDE,
  lyingOnStomach: ActionColors.COLOR_LYING_ON_STOMACH,
  lyingOnBack: ActionColors.COLOR_LYING_ON_BACK,
  target: ActionColors.COLOR_TARGET,
  mock: ActionColors.COLOR_TARGET,
};

const mockActivityColor = {
  walking: ActionColors.COLOR_TARGET,
  standing: ActionColors.COLOR_TARGET,
  sitting: ActionColors.COLOR_TARGET,
  lying: ActionColors.COLOR_TARGET,
  lyingOnLeft: ActionColors.COLOR_TARGET,
  lyingOnRight: ActionColors.COLOR_TARGET,
  lyingOnStomach: ActionColors.COLOR_TARGET,
  lyingOnBack: ActionColors.COLOR_TARGET,
  target: ActionColors.COLOR_TARGET,
  mock: ActionColors.COLOR_TARGET,
};

const vitalSignColor = {
  normal: '#4CB6AC',
  notgood: '#EFA12D',
  severe: '#D30B0B',
};

const activityKey = ['lying', 'sitting', 'standing', 'walking'];

const sleepKey = ['lyingOnBack', 'lyingOnStomach', 'lyingOnLeft', 'lyingOnRight'];

const formatNumber = (num) => {
  let NEWnum;
  switch (num) {
    case undefined:
      NEWnum = 0;
      break;
    case null:
      NEWnum = 0;
      break;
    case '':
      NEWnum = 0;
      break;
    default:
      NEWnum = num;
  }
  return NEWnum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const hhmmss = (secs) => {
  let nan = isNaN(secs);
  if (!nan) {
    let minutes = Math.floor(secs / 60);
    secs = secs % 60;
    let hours = Math.floor(minutes / 60);
    hours = hours > 24 ? 24 : hours;
    minutes = minutes % 60;
    let minutesFormat = `${minutes === 0 ? '00' : minutes < 10 ? '0' + minutes : minutes}`;
    let hoursFormat = `${hours === 0 ? '00' : hours < 10 ? '0' + hours : hours}`;
    return `${hoursFormat}:${minutesFormat}`;
  }
};

const calculateBmr = (gender, weight, height, age) => {
  if (gender === 'M') {
    const w = weight > 0 ? weight : 68;
    const h = height > 0 ? height : 170;
    return 10 * w + 6.25 * h - 5 * age + 5;
  } else {
    const w = weight > 0 ? weight : 57;
    const h = height > 0 ? height : 157;
    return 10 * w + 6.25 * h - 5 * age - 161;
  }
};

const convertedSleepData = (data) => {
  let convertedData = [];
  let mockDate = [];
  let sumSleepValue = 0;
  let sumActivityValue = 0;

  if (data) {
    for (const [key, value] of Object.entries(data)) {
      if (sleepKey.indexOf(key) >= 0) {
        convertedData.push({
          value: value > 0 ? value / 3600 : value,
          label: key,
          text: hhmmss(value),
        });
        sumSleepValue += value;
      }
      if (activityKey.indexOf(key) >= 0) {
        sumActivityValue += value;
      }
    }
  }

  if (sumSleepValue === 0) {
    convertedData.push({
      value: 100,
      label: 'mock',
    });
  }
  sleepKey.map((val) => {
    mockDate.push({
      value: 0,
      label: val,
    });
    return val;
  });
  sumSleepValue = hhmmss(sumSleepValue);
  sumActivityValue = hhmmss(sumActivityValue);
  return { convertedData, sumSleepValue, sumActivityValue, mockDate };
};

const convertedEnergyData = (data, bmr, duration) => {
  let convertedDuration = [];
  let convertedData = [];
  let mockDate = [];
  // let target = 0;
  if (duration) {
    for (const [key, value] of Object.entries(duration)) {
      if (activityKey.indexOf(key) >= 0) {
        convertedDuration.push({
          value: value > 0 ? value / 3600 : value,
          label: key,
          text: hhmmss(value),
        });
      }
    }
  }

  if (data) {
    // target = data.total - bmr;
    for (const [key, value] of Object.entries(data)) {
      key !== 'total' &&
        convertedData.push({
          value: value,
          label: key,
          text: formatNumber(value),
        });
    }
  } else {
    convertedData.push({
      value: 100,
      label: 'target',
    });
  }
  activityKey.map((val) => {
    mockDate.push({
      value: 0,
      label: val,
    });
    return val;
  });
  return { convertedData, mockDate, convertedDuration };
};

const RouterPlace = (serial, routers, t) => {
  const routerData = (routers || []).find((val) => val.serial === serial);
  if (routerData) {
    return routerData.place;
  } else {
    return `${t('unknown')}`;
  }
};

const TextValue = (value, t) => {
  // const { t } = props;
  let result = `${t('unknown')}`;
  switch (value) {
    case 1:
      result = `${t('lying')}`;
      break;
    case 2:
      result = `${t('sitting')}`;
      break;
    case 3:
      result = `${t('standing')}`;
      break;
    case 4:
      result = `${t('walking')}`;
      break;
    case 5:
      result = `${t('Running')}`;
      break;
    case 6:
      result = `${t('Jumping')}`;
      break;
    case 7:
      result = `${t('movement')}`;
      break;
    case 8:
      result = `${t('low_battery')}`;
      break;
    case 9:
      result = `${t('no_signal')}`;
      break;
    case 10:
      result = `${t('not_turn_for')}`;
      break;
    case 11:
      result = `${t('no_movement')}`;
      break;
    case 12:
      result = `${t('falling')}`;
      break;
    case 13:
      result = `${t('unknown')}`;
      break;
    case 14:
      result = `${t('lying_on_back')}`;
      break;
    case 15:
      result = `${t('lying_on_stomach')}`;
      break;
    case 16:
      result = `${t('lying_on_left_side')}`;
      break;
    case 17:
      result = `${t('lying_on_right_side')}`;
      break;
    case 18:
      result = `${t('out_of_area')}`;
      break;
    case 19:
      result = `${t('walking_at_night')}`;
      break;
    case 100:
      result = `${t('detect_location')}`;
      break;
    // case 101:
    //   result = `${t("alert_emergency_alarm")}`;
    //   break;
    default:
      break;
  }
  return result;
};

const DateTime = (date, t) => {
  const _date = getTimeFormatted(date, t);
  return (
    <div>
      {t('alert_last_seen')} @ {_date}
    </div>
  );
};

const Note = (props) => (
  <div className='block h-full pt-5 break-words overflow-clip font-xs w-400'>
    {props.disabled ? (
      <div className='w-11/12 text-sm font-medium capitalize resize-none h-3/5 '>{props.text}</div>
    ) : (
      <textarea
        className='w-11/12 text-sm font-medium capitalize resize-none h-3/5 '
        // onChange={(e) => props.onChange("note", e.target.value)}
        // value={props.text}
      />
    )}
  </div>
);

const ActivityImg = (props) => <div className='space-y-1'>{props.img}</div>;

const ActivityDescribtionText = (props) => (
  <div className='mt-1 space-y-1'>
    <div className='block'>
      <span className='text-2xl text-bold text-summary-header'>{props.upperText}</span> {props.upperEndText}
    </div>
    {props.nosignal === 'true' && <div className='block text-xs'>{props.middleText}</div>}
    {props.lowerText ? (
      <ul className='block ml-4 text-xs list-disc text-normal'>
        <li>{props.lowerText}</li>
      </ul>
    ) : (
      ''
    )}
  </div>
);

const Vitalsign = (props) => (
  <div className='px-6 py-4 md:mr-0 lg:mr-4 xl:mr-20'>
    <div className='max-w-xs text-sm font-normal capitalize text-secondary '>{props.title}</div>
    <div className='py-2 text-3xl font-medium text-summary-header'>
      {props.value}
      {props.value !== 'N/A' && <span className='ml-2 text-sm font-semibold '>{props.unit}</span>}
    </div>
    {props.value && props.value !== 'N/A' && props.status && props.status.result !== '' ? (
      <div className='flex flex-row my-auto'>
        <div className='mt-2px'>
          <svg width={10} height={10}>
            <circle cx='50%' cy='50%' r='5' fill={vitalSignColor[props.status ? props.status.color : '']} />
          </svg>
        </div>
        <div className={`ml-2 text-xs font-semibold ${props.status ? props.status.color : ''}`}>
          {props.status && props.status.result}
        </div>
      </div>
    ) : (
      ''
    )}
  </div>
);

// const ActivityPlaceholder = (props) => (
//   <div className="grid h-full grid-cols-3 grid-rows-2 gap-1 p-0 ">
//     <div className="col-span-1 row-span-2 row-start-0">{props.graphData}</div>
//     <div className="col-span-2 col-start-2 row-span-2 row-start-0 ">
//       {props.graphDesc}
//     </div>
//   </div>
// );

function Summary(props) {
  const { t } = props;
  let bmr = 0;
  if (props.patient) {
    bmr = calculateBmr(props.patient.gender, props.patient.weight, props.patient.height, props.patient.age);
  }

  const energyData = convertedEnergyData(props.data.summary.energy, bmr, props.data.summary.duration);
  const sleepData = convertedSleepData(props.data.summary.duration);

  let area = '';
  if (props.alertType === 510) {
    area = 'In Area.';
  }
  if (props.alertType === 509) {
    area = 'Out Area.';
  }

  let activityValue = 0;
  let dateTime = new Date();
  let routerSerial = null;
  let action = null;
  const deviceWithAirnode =
    props.devices && props.devices.length > 0
      ? props.devices.find((d) => d.deviceVersion && d.deviceVersion.category === 'AIRNODE')
      : null;
  const deviceWithEmergency =
    props.devices && props.devices.length > 0
      ? props.devices.find((d) => d.deviceVersion && d.deviceVersion.category === 'EMERGENCY_BUTTON')
      : null;
  const otherDevice =
    props.devices && props.devices.length > 0
      ? props.devices.find(
          (d) =>
            d.deviceVersion &&
            d.deviceVersion.category !== 'EMERGENCY_BUTTON' &&
            d.deviceVersion.category !== 'AIRNODE',
        )
      : null;

  const device = deviceWithAirnode ? deviceWithAirnode : deviceWithEmergency ? deviceWithEmergency : otherDevice;
  if (device && props.action && device.action) {
    action = props.action.timestamp > device.action.timestamp ? props.action : device.action;
    if (action) {
      if (device.status === 'ACTIVATE_ON' && action.changed.a && action.changed.a !== 9) {
        activityValue = action.changed.a;
        dateTime = action.timestamp;
        routerSerial = action.routerSerial;
      }
    }
  }

  const energyTotal = props.data.summary.energy && props.data.summary.energy.total;
  const durationLying = props.data.summary.duration && props.data.summary.duration.lying;

  return (
    <div className='flex-wrap flex-auto min-w-0 px-16'>
      {props.role === 'Demo' ? (
        <NoDataBox text={t('no_data')} />
      ) : (
        <div className='block'>
          <div className='inset-x-0 flex-wrap justify-center w-full col-span-12 xl:flex lg:flex md:block sm:block'>
            <div className='flex-grow col-span-12 pt-1 lg:col-span-6 md:col-span-6 sm:col-span-12'>
              <ActivityLargeBox
                header={t('activity_summary')}
                graphCenter={
                  <ActivityCenterComponent
                    total={props.data.summary.energy ? props.data.summary.energy.total : 0}
                    goal={bmr}
                    t={t}
                  />
                }
                graphData={
                  <ActivityChart
                    data={energyData.convertedData}
                    total={energyTotal}
                    colorset={!energyTotal ? mockActivityColor : activityColor}
                    chartId='activity-summary'
                  />
                }
                graphLegend={
                  <ActivityLegend
                    t={t}
                    data={!energyTotal ? energyData.mockDate : energyData.convertedDuration}
                    total={energyTotal}
                    colorset={!energyTotal ? mockActivityColor : activityColor}
                    unit={'Cal'}
                  />
                }
              />
            </div>
            <div className='flex-grow col-span-12 pt-1 lg:col-span-6 md:col-span-6 sm:col-span-12'>
              <ActivityLargeBox
                header={t('sleep_summary')}
                graphData={
                  <ActivityChart data={sleepData.convertedData} chartId='sleep-summary' colorset={activityColor} />
                }
                graphCenter={<SleepCenterComponent total={sleepData.sumSleepValue} t={t} />}
                graphLegend={
                  <SleepLegend
                    t={t}
                    data={durationLying ? sleepData.convertedData : sleepData.mockDate}
                    colorset={!durationLying ? mockActivityColor : activityColor}
                    unit={'hours'}
                  />
                }
              />
            </div>
          </div>

          <div className='inset-x-0 flex flex-wrap justify-center w-full col-span-12 mt-3'>
            <div className='flex-grow col-span-4 px-3 py-2'>
              <div className='col-span-12 mb-4'>
                <ActivitySmallBox
                  header={t('step')}
                  img={<ActivityImg img={<img src={IconStep} alt='' />} />}
                  value={formatNumber(props.data.summary.steps)}
                  subtitle={t('steps')}
                />
              </div>

              <div className='col-span-12'>
                <ActivitySmallBox
                  header={t('distance')}
                  img={<ActivityImg img={<img src={IconDistance} alt='' />} />}
                  value={props.data.summary.distance ? (props.data.summary.distance / 1000).toFixed(2) : 0}
                  subtitle={t('km')}
                />
              </div>
            </div>

            <div className='flex-grow col-span-4 px-3 py-2'>
              <ActivityMediumBox header={`${t('activity')}/${t('location')}`}>
                <div className='h-173'>
                  <div className='flex my-8'>
                    <div className='flex items-center justify-center w-28'>
                      <ActivityImg img={<Accessibility>{activityValue}</Accessibility>} />
                    </div>
                    <div className='flex'>
                      <ActivityDescribtionText
                        upperText={TextValue(activityValue, props.t)}
                        middleText={DateTime(dateTime, props.t)}
                        nosignal={action && action.nosignal}
                      />
                    </div>
                  </div>
                  <div className='flex my-5'>
                    <div className='flex items-center justify-center w-28'>
                      <ActivityImg img={<img src={IconPin} alt='' />} />
                    </div>
                    <div className='flex'>
                      <ActivityDescribtionText
                        upperText={RouterPlace(routerSerial, props.routers, props.t)}
                        middleText={DateTime(dateTime, props.t)}
                        lowerText={area}
                        nosignal={action && action.nosignal}
                      />
                    </div>
                  </div>
                </div>
              </ActivityMediumBox>
            </div>

            <div className='flex-grow col-span-4 px-3 py-2'>
              <ActivityMediumBox header={t('note')}>
                <div className='h-205'>
                  <Note
                    text={props.note.data.patient.note}
                    note={props.patient && props.patient.note}
                    disabled={props.toggle}
                    onChange={props.onNoteChange}
                  />
                </div>
              </ActivityMediumBox>
            </div>
          </div>
          <div className='mb-10'>
            <ActivityLongBox
              header={t('vitalsign')}
              title={t('recorded_on')}
              time={getTimeFormatted(props.vitalsign.createdDate)}
              display={props.vitalsign.createdDate}
            >
              <Vitalsign
                title={t('body_temperature')}
                value={
                  props.vitalsign.temperature === null || props.vitalsign.temperature === ''
                    ? 'N/A'
                    : props.vitalsign.temperature
                }
                status={TemperatureLevel(props.vitalsign.temperature || 0, t)}
                unit={t('celsius')}
                t={t}
              />
              <Vitalsign
                title={`${t('blood_pressure')}`}
                value={`${props.vitalsign.bloodPressure === null || props.vitalsign.bloodPressure === '' ? 'N' : props.vitalsign.bloodPressure.systolic}/${props.vitalsign.bloodPressure === null || props.vitalsign.bloodPressure === '' ? 'A' : props.vitalsign.bloodPressure.diastolic}`}
                status={BloodPressureLevel(
                  (props.vitalsign.bloodPressure && props.vitalsign.bloodPressure.systolic) || 0,
                  (props.vitalsign.bloodPressure && props.vitalsign.bloodPressure.diastolic) || 0,
                  t,
                )}
                unit={t('mmhg')}
                t={t}
              />
              <Vitalsign
                title={t('heartrate')}
                value={
                  props.vitalsign.heartrate === null || props.vitalsign.heartrate === ''
                    ? 'N/A'
                    : props.vitalsign.heartrate
                }
                status={HeartRateLevel(props.vitalsign.heartrate || 0, t)}
                unit={t('bpm')}
                t={t}
              />
              <Vitalsign
                title={t('spo2')}
                value={props.vitalsign.spo2 === null || props.vitalsign.spo2 === '' ? 'N/A' : props.vitalsign.spo2}
                status={SpO2Level(props.vitalsign.spo2 || 0, t)}
                unit='%'
                t={t}
              />
              <Vitalsign
                title={t('respiratory_rate')}
                value={
                  props.vitalsign.respirationRate === null || props.vitalsign.respirationRate === ''
                    ? 'N/A'
                    : props.vitalsign.respirationRate
                }
                // status={RespirationRateLevel(props.vitalsign.respirationRate || 0, t)}
                unit={t('bpm')}
                t={t}
              />
            </ActivityLongBox>
          </div>
        </div>
      )}
    </div>
  );
}

export default Summary;
